/**
 * WCC360 v2 — Second Opinion (X-OBSERVER-001, CANON-PENDING).
 *
 * A parent, guardian or mentor has watched this person for years. A 45-item
 * walk samples them for twelve minutes. That asymmetry is real, and ignoring
 * it wastes the single richest source of behavioural recall we have access to.
 *
 * But it is also the most dangerous evidence in the product, for two reasons:
 *
 *   1. A parent's read of a virtue ("she's a natural leader") has exactly the
 *      right-answer problem that killed v1's Likert items. So we NEVER ask an
 *      observer to rate a trait, a competence or an archetype. We ask only
 *      what they have *seen*: what the person loses track of time doing, what
 *      they say they care about, what they said they wanted to be.
 *
 *   2. It is not first-person. A young person's own account of themselves must
 *      always outrank someone else's account of them — including when the
 *      someone else is right.
 *
 * So the whole channel is bounded four ways:
 *
 *   - CONSENT. Nothing an observer writes enters the evidence layer until the
 *     learner has read it and approved that specific item. Discarded items are
 *     never scored, never shown, never counted.
 *   - NUDGE ONLY. Approved items contribute family affinity through the same
 *     bounded channel passions and causes already use. They contribute NO
 *     archetype points — an observer can widen the pool of work a learner
 *     sees, and cannot change who the engine says they are.
 *   - HALF WEIGHT, HARD CAP. Each approved item is worth
 *     `OBSERVER_ITEM_WEIGHT` (half a first-person pick), and the total across
 *     every approved item is capped at `OBSERVER_TOTAL_CAP`. First-person
 *     evidence always dominates.
 *   - NEVER ELIGIBILITY. No gate reads observer input. An observation can
 *     never delete a career from the pool, and never gates a tier.
 *
 * Disagreement is signal, not noise: where an observer names something the
 * learner did not, we surface it as a suggestion and label the source in
 * "Why you matched" — never as the learner's own words.
 */
import { PASSIONS, NONE_PASSION_ID, type Passion } from "@/data/passions";
import { CAUSES, HOBBIES } from "./causes";
import type { Band } from "./band";

// ---- limits --------------------------------------------------------------

/** Observations an observer may offer, per kind. Small on purpose: a long
 *  form gets abandoned, and a shortlist forces a real choice. */
export const OBSERVER_MAX_PASSIONS = 5;
export const OBSERVER_MAX_CAUSES = 3;
export const OBSERVER_NOTE_MAX = 600;
export const OBSERVER_DREAM_MAX = 120;

/** Open invites a learner may hold at once. Mirrors the database guard. */
export const OBSERVER_MAX_OPEN_INVITES = 3;

/** Affinity a single approved observer item carries. Half a learner's own
 *  pick — present, never louder. */
export const OBSERVER_ITEM_WEIGHT = 0.5;

/** Ceiling on the total affinity every approved item can contribute, summed
 *  across families. Below the weight of two first-person picks. */
export const OBSERVER_TOTAL_CAP = 2;

// ---- relationships ------------------------------------------------------

export type ObserverRelationship = "parent" | "guardian" | "mentor" | "someone_close";

export const OBSERVER_RELATIONSHIPS: Array<{
  id: ObserverRelationship; label: string; hint: string;
}> = [
  { id: "parent",        label: "A parent",          hint: "Mum, dad, or both" },
  { id: "guardian",      label: "A guardian",        hint: "Whoever looks after you at home" },
  { id: "mentor",        label: "A teacher or coach", hint: "Someone who has taught or trained you" },
  { id: "someone_close", label: "Someone close to you", hint: "An older sibling, a family friend" },
];

export function relationshipLabel(r: ObserverRelationship | string | undefined): string {
  return OBSERVER_RELATIONSHIPS.find((x) => x.id === r)?.label ?? "Someone close to you";
}

/** How the observer is described back to the learner, mid-sentence. */
export function relationshipNoun(r: ObserverRelationship | string | undefined): string {
  switch (r) {
    case "parent": return "your parent";
    case "guardian": return "your guardian";
    case "mentor": return "your teacher or coach";
    default: return "someone who knows you well";
  }
}

// ---- what the observer is asked ----------------------------------------

export interface ObserverPrompt {
  id: "lose_track" | "cares_about" | "wanted_to_be" | "seen_them_do";
  question: string;
  help: string;
  maxLength: number;
}

/**
 * Four open prompts, all behavioural recall. Not one of them asks for a
 * rating, a score, or a judgement of character — that is the point.
 */
export const OBSERVER_PROMPTS: ObserverPrompt[] = [
  {
    id: "lose_track",
    question: "What do they lose track of time doing?",
    help: "The thing you have to call them away from. Not what they're good at — what absorbs them.",
    maxLength: OBSERVER_NOTE_MAX,
  },
  {
    id: "cares_about",
    question: "What do they get worked up about in the world?",
    help: "A cause, an unfairness, a problem they bring up at the dinner table.",
    maxLength: OBSERVER_NOTE_MAX,
  },
  {
    id: "wanted_to_be",
    question: "What have they said they wanted to be?",
    help: "At any age. Even the one everybody laughed about.",
    maxLength: OBSERVER_DREAM_MAX,
  },
  {
    id: "seen_them_do",
    question: "Describe one thing you've watched them do well",
    help: "A specific moment, not a quality. What happened, and what they did.",
    maxLength: OBSERVER_NOTE_MAX,
  },
];

/** The passion catalogue an observer chooses from — the learner's own set. */
export function observerPassionCatalogue(): Passion[] {
  return PASSIONS.filter((p) => p.id !== NONE_PASSION_ID);
}
export function observerCauseCatalogue(): Passion[] { return CAUSES; }
export function observerHobbyCatalogue(): Passion[] { return HOBBIES; }

// ---- the payloads -------------------------------------------------------

/** What the observer submitted. Raw, unapproved, unscored. */
export interface ObserverObservation {
  relationship: ObserverRelationship;
  observer_name?: string;
  /** Activities they have watched this person choose to do. */
  passion_ids: string[];
  /** Causes they have heard this person care about, in the observer's order. */
  cause_ids: string[];
  /** Free-text answers keyed by prompt id. Context in the report; never scored. */
  notes: Partial<Record<ObserverPrompt["id"], string>>;
  submitted_at: string;
}

/** What the learner approved. The ONLY shape the engine ever reads. */
export interface ApprovedObserverInput {
  relationship: ObserverRelationship;
  observer_name?: string;
  /** Subset of the observation the learner ticked. */
  passion_ids: string[];
  cause_ids: string[];
  /** Approved verbatim note ids — carried into the report as context only. */
  note_ids: ObserverPrompt["id"][];
  approved_at: string;
  /** Every item the learner explicitly rejected. Kept so we never re-ask. */
  declined_passion_ids?: string[];
  declined_cause_ids?: string[];
  /** The note wording as the learner left it — theirs wins over the original. */
  edited_notes?: Partial<Record<ObserverPrompt["id"], string>>;
  edited_note_ids?: ObserverPrompt["id"][];
}

export function emptyApproval(
  relationship: ObserverRelationship,
  observer_name?: string,
): ApprovedObserverInput {
  return {
    relationship,
    observer_name,
    passion_ids: [],
    cause_ids: [],
    note_ids: [],
    approved_at: new Date().toISOString(),
  };
}

// ---- the learner's working copy, before approval ------------------------

/**
 * The learner's editable copy of an observation.
 *
 * Approval is not a yes/no on someone else's text. A parent can be half-right
 * — right that you draw, wrong about why — and forcing an all-or-nothing
 * choice throws away the true half. So the learner works on a draft: retract
 * anything that isn't them, reword any note in their own words, put back
 * anything they retracted by mistake. Nothing here is scored. Only the
 * approval built from this draft ever reaches the engine.
 *
 * The observation itself is never mutated: the original stays on the row, so
 * "what they actually said" and "what I kept" are always both auditable.
 */
export interface ObserverDraft {
  passion_ids: string[];
  cause_ids: string[];
  notes: Partial<Record<ObserverPrompt["id"], string>>;
  /** Retracted, not deleted — a retraction is reversible until approval. */
  retracted_passion_ids: string[];
  retracted_cause_ids: string[];
  /** Notes the learner reworded, so the report can say so honestly. */
  edited_note_ids: ObserverPrompt["id"][];
  updated_at: string;
}

export type ObserverEditAction =
  | "received" | "retracted" | "restored" | "edited_note"
  | "cleared_note" | "approved" | "declined";

/** One entry in the change history the learner can read back. */
export interface ObserverEditEvent {
  at: string;
  action: ObserverEditAction;
  /** Passion/cause id or note prompt id this touched, where it applies. */
  target?: string;
  /** Human label for the target, so history reads without a lookup table. */
  label?: string;
  /** Free detail, e.g. how many items an approval carried. */
  detail?: string;
}

export const EDIT_ACTION_LABEL: Record<ObserverEditAction, string> = {
  received: "They sent this",
  retracted: "You took this out",
  restored: "You put this back",
  edited_note: "You reworded this",
  cleared_note: "You removed these words",
  approved: "You approved what was left",
  declined: "You set the whole thing aside",
};

export function draftFromObservation(o: ObserverObservation): ObserverDraft {
  return {
    passion_ids: [...o.passion_ids],
    cause_ids: [...o.cause_ids],
    notes: { ...o.notes },
    retracted_passion_ids: [],
    retracted_cause_ids: [],
    edited_note_ids: [],
    updated_at: new Date().toISOString(),
  };
}

/** A stored draft, repaired against the observation it belongs to. */
export function hydrateDraft(
  o: ObserverObservation,
  stored: ObserverDraft | null | undefined,
): ObserverDraft {
  if (!stored || !Array.isArray(stored.passion_ids)) return draftFromObservation(o);
  const known = new Set([...o.passion_ids, ...o.cause_ids]);
  return {
    passion_ids: stored.passion_ids.filter((id) => o.passion_ids.includes(id)),
    cause_ids: (stored.cause_ids ?? []).filter((id) => o.cause_ids.includes(id)),
    notes: stored.notes ?? {},
    retracted_passion_ids: (stored.retracted_passion_ids ?? []).filter((id) => known.has(id)),
    retracted_cause_ids: (stored.retracted_cause_ids ?? []).filter((id) => known.has(id)),
    edited_note_ids: stored.edited_note_ids ?? [],
    updated_at: stored.updated_at ?? new Date().toISOString(),
  };
}

export function retractFromDraft(d: ObserverDraft, id: string): ObserverDraft {
  const isCause = d.cause_ids.includes(id);
  return {
    ...d,
    passion_ids: d.passion_ids.filter((x) => x !== id),
    cause_ids: d.cause_ids.filter((x) => x !== id),
    retracted_passion_ids: isCause ? d.retracted_passion_ids : [...new Set([...d.retracted_passion_ids, id])],
    retracted_cause_ids: isCause ? [...new Set([...d.retracted_cause_ids, id])] : d.retracted_cause_ids,
    updated_at: new Date().toISOString(),
  };
}

export function restoreToDraft(d: ObserverDraft, id: string): ObserverDraft {
  const isCause = d.retracted_cause_ids.includes(id);
  return {
    ...d,
    passion_ids: isCause ? d.passion_ids : [...new Set([...d.passion_ids, id])],
    cause_ids: isCause ? [...new Set([...d.cause_ids, id])] : d.cause_ids,
    retracted_passion_ids: d.retracted_passion_ids.filter((x) => x !== id),
    retracted_cause_ids: d.retracted_cause_ids.filter((x) => x !== id),
    updated_at: new Date().toISOString(),
  };
}

export function editNoteInDraft(
  d: ObserverDraft, id: ObserverPrompt["id"], text: string,
): ObserverDraft {
  return {
    ...d,
    notes: { ...d.notes, [id]: text },
    edited_note_ids: [...new Set([...d.edited_note_ids, id])],
    updated_at: new Date().toISOString(),
  };
}

export function draftItemCount(d: ObserverDraft): number {
  return d.passion_ids.length + d.cause_ids.length;
}

/** Has the learner changed anything from what the observer sent? */
export function draftIsEdited(o: ObserverObservation, d: ObserverDraft): boolean {
  return (
    d.retracted_passion_ids.length > 0 ||
    d.retracted_cause_ids.length > 0 ||
    d.edited_note_ids.length > 0
  );
}

/**
 * The approval built from the draft. Every item still standing counts; every
 * retraction is recorded as a decline so we never re-ask, and so the report
 * can show what was left out as well as what went in.
 */
export function approvalFromDraft(
  o: ObserverObservation,
  d: ObserverDraft,
): ApprovedObserverInput {
  const notes = { ...d.notes };
  return {
    relationship: o.relationship,
    observer_name: o.observer_name,
    passion_ids: [...d.passion_ids],
    cause_ids: [...d.cause_ids],
    note_ids: OBSERVER_PROMPTS
      .filter((p) => (notes[p.id] ?? "").trim().length > 0)
      .map((p) => p.id),
    approved_at: new Date().toISOString(),
    declined_passion_ids: [...d.retracted_passion_ids],
    declined_cause_ids: [...d.retracted_cause_ids],
    edited_notes: notes,
    edited_note_ids: [...d.edited_note_ids],
  };
}

export function approvedItemCount(input: ApprovedObserverInput | undefined): number {
  if (!input) return 0;
  return input.passion_ids.length + input.cause_ids.length;
}

export function hasApprovedObserverInput(
  input: ApprovedObserverInput | undefined,
): boolean {
  return approvedItemCount(input) > 0;
}

// ---- scoring: affinity only, half weight, hard cap ---------------------

const byId = (id: string): Passion | undefined => PASSIONS.find((p) => p.id === id);

/**
 * Family affinity from approved observer items.
 *
 * Same shape as `weightedPassionFamilyCounts` — a family → strength map read
 * by `scorePassion` — so nothing new enters the six scoring slots. Two hard
 * bounds live here and nowhere else:
 *
 *   - every item is worth `OBSERVER_ITEM_WEIGHT`, half a first-person pick;
 *   - the summed strength is scaled down to `OBSERVER_TOTAL_CAP` if the
 *     observer named a lot, so a chatty parent cannot out-vote a quiet
 *     learner.
 *
 * `firstPerson` is the learner's own family strength map. Where the learner
 * already named a family, the observer adds NOTHING — corroboration must not
 * double-count, or agreement would quietly become the loudest signal in the
 * model.
 */
export function observerFamilyCounts(
  input: ApprovedObserverInput | undefined,
  firstPerson?: Map<string, number>,
): Map<string, number> {
  const out = new Map<string, number>();
  if (!input) return out;

  const ids = [...input.passion_ids, ...input.cause_ids];
  for (const id of ids) {
    const p = byId(id);
    if (!p) continue;
    for (const f of p.families) {
      if (firstPerson?.has(f)) continue; // already the learner's own words
      out.set(f, (out.get(f) ?? 0) + OBSERVER_ITEM_WEIGHT);
    }
  }

  const total = [...out.values()].reduce((s, v) => s + v, 0);
  if (total > OBSERVER_TOTAL_CAP) {
    const scale = OBSERVER_TOTAL_CAP / total;
    for (const [f, v] of out) out.set(f, v * scale);
  }
  return out;
}

/** Families the approved observer input points at, for the affinity set. */
export function observerFamilies(
  input: ApprovedObserverInput | undefined,
  firstPerson?: Map<string, number>,
): string[] {
  return [...observerFamilyCounts(input, firstPerson).keys()].sort();
}

/**
 * Explanation labels, keyed by family. Used to say *whose* observation
 * surfaced a career — in the honest form: they noticed, you confirmed.
 */
export function observerFamilyLabels(
  input: ApprovedObserverInput | undefined,
  firstPerson?: Map<string, number>,
): Map<string, { label: string; noun: string }> {
  const out = new Map<string, { label: string; noun: string }>();
  if (!input) return out;
  const noun = relationshipNoun(input.relationship);
  const eligible = new Set(observerFamilies(input, firstPerson));
  for (const id of [...input.passion_ids, ...input.cause_ids]) {
    const p = byId(id);
    if (!p) continue;
    for (const f of p.families) {
      if (!eligible.has(f) || out.has(f)) continue;
      out.set(f, { label: p.label, noun });
    }
  }
  return out;
}

/**
 * Items the observer named that the learner did not — the genuinely new
 * information, and the only part worth a review screen.
 */
export function divergentItems(
  observation: ObserverObservation | undefined,
  learnerPassionIds: string[] | undefined,
  learnerCauseIds: string[] | undefined,
): { passions: Passion[]; causes: Passion[] } {
  if (!observation) return { passions: [], causes: [] };
  const own = new Set([...(learnerPassionIds ?? []), ...(learnerCauseIds ?? [])]);
  return {
    passions: observation.passion_ids
      .filter((id) => !own.has(id))
      .map(byId)
      .filter((p): p is Passion => Boolean(p)),
    causes: observation.cause_ids
      .filter((id) => !own.has(id))
      .map(byId)
      .filter((p): p is Passion => Boolean(p)),
  };
}

// ---- band copy ----------------------------------------------------------

/** Who we suggest asking, per band. 18+ is not a parent question. */
export function observerAskCopy(band: Band): { title: string; body: string } {
  switch (band) {
    case "12to14":
      return {
        title: "Ask a grown-up what they've noticed",
        body: "They've watched you longer than any test can. You get to read every word they send and decide what counts.",
      };
    case "15to18":
      return {
        title: "Get a second opinion",
        body: "A parent, a teacher or a coach has seen you in rooms this walk can't reach. You approve every line before it counts — and you can bin the lot.",
      };
    default:
      return {
        title: "Ask someone who knows you well",
        body: "A partner, a mentor, an old manager. They notice patterns you're too close to see. Nothing they send counts until you say so.",
      };
  }
}
